import { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Trash2, Loader2, Inbox, Copy, Check, RefreshCw, Pencil, Eye, EyeOff, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { renderCell } from "@/lib/render";
import { RESOURCES } from "@/config/resources";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { KeystoreDownloadButton, KeystoreImportButton } from "@/components/KeystoreDialogs";
import { ProcessorKeysButton } from "@/components/ProcessorKeysDialog";
import { CryptoTxDetailsButton } from "@/components/CryptoTxDetails";

// Always coerce an API response into an array so table/select .map() can't throw.
// Handles raw arrays, common wrappers ({items|results|data|rows}), and anything else -> [].
function toArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    for (const k of ["items", "results", "data", "rows"]) {
      if (Array.isArray(data[k])) return data[k];
    }
  }
  return [];
}

export default function ResourcePage({ resourceKey }) {
  const cfg = RESOURCES[resourceKey];
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [lookups, setLookups] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [revealKey, setRevealKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [revealedSecrets, setRevealedSecrets] = useState({});

  const toggleSecret = (id) => setRevealedSecrets((p) => ({ ...p, [id]: !p[id] }));

  const canWrite = cfg.createRoles.includes(user.role);
  const isApiKeys = resourceKey === "api-keys";
  const isCurrencies = resourceKey === "currencies";
  const isCryptoWallets = resourceKey === "crypto-wallets";

  const refreshRates = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/currencies/refresh-rates");
      const msg = `Updated ${data.updated_fiat} fiat + ${data.updated_crypto} crypto rates`;
      if (data.errors?.length) toast.warning(`${msg}. Some sources failed: ${data.errors.join("; ")}`);
      else toast.success(`${msg} · FX as of ${data.source_updated_at}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(cfg.endpoint);
      const list = toArray(data);
      if (cfg.sortBy) list.sort((a, b) => String(a[cfg.sortBy]).localeCompare(String(b[cfg.sortBy]), undefined, { numeric: true }));
      setRows(list);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setRows([]);
    }
  }, [cfg.endpoint, cfg.sortBy]);

  useEffect(() => { load(); }, [load]);

  // Live-poll API key usage so rate-limit alerts stay current
  useEffect(() => {
    if (!isApiKeys) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [isApiKeys, load]);

  // Keys at/near their per-minute rate limit
  const rateAlerts = useMemo(() => {
    if (!isApiKeys || !rows) return [];
    return rows
      .filter((r) => r.status === "active" && Number(r.rate_limit_per_min) > 0)
      .map((r) => ({
        ...r,
        pct: Math.min(100, Math.round((Number(r.recent_calls || 0) / Number(r.rate_limit_per_min)) * 100)),
      }))
      .filter((r) => r.pct >= 80)
      .sort((a, b) => b.pct - a.pct);
  }, [isApiKeys, rows]);

  // Preload lookup sources for form selects
  useEffect(() => {
    const sources = new Set();
    (cfg.fields || []).forEach((f) => {
      if (f.source) sources.add(f.source);
      if (f.type === "ownerSelect") { sources.add("companies"); sources.add("customers"); }
    });
    sources.forEach(async (s) => {
      try {
        const { data } = await api.get(`/${s}`);
        setLookups((prev) => ({ ...prev, [s]: toArray(data) }));
      } catch { /* role may lack access */ }
    });
  }, [cfg.fields]);

  const openCreate = () => {
    const init = {};
    (cfg.fields || []).forEach((f) => {
      if (f.options) init[f.name] = f.options[0].value;
      else if (f.type === "number") init[f.name] = 0;
      else init[f.name] = "";
    });
    setEditingId(null);
    setForm(init);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const init = {};
    (cfg.fields || []).forEach((f) => {
      let v = row[f.name];
      if (f.name === "scopes" && Array.isArray(v)) v = v[0] || "read";
      if (v === undefined || v === null) v = f.type === "number" ? 0 : "";
      init[f.name] = v;
    });
    setEditingId(row.id);
    setForm(init);
    setDialogOpen(true);
  };

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const updateUrl = () => {
    if (resourceKey === "users") return { method: "patch", url: `/users/${editingId}` };
    if (isApiKeys) return { method: "patch", url: `/api-keys/${editingId}` };
    return { method: "put", url: `${cfg.endpoint}/${editingId}` };
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      (cfg.fields || []).forEach((f) => {
        if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
        if ((payload[f.name] === "" || payload[f.name] == null) && (f.optional)) delete payload[f.name];
      });
      if (payload.scopes && !Array.isArray(payload.scopes)) payload.scopes = [payload.scopes];
      // On edit, a blank user password means "leave unchanged"
      if (editingId && resourceKey === "users" && !payload.password) delete payload.password;

      if (editingId) {
        const { method, url } = updateUrl();
        await api[method](url, payload);
        toast.success(`${cfg.title.replace(/s$/, "")} updated`);
      } else {
        const { data } = await api.post(cfg.endpoint, payload);
        if (isApiKeys && data.generated_key) setRevealKey(data.generated_key);
        toast.success(`${cfg.title.replace(/s$/, "")} created`);
      }
      setDialogOpen(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`${cfg.endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doToggle = async (row, next) => {
    const t = cfg.toggle;
    try {
      await api.patch(`${t.endpoint}/${row.id}/toggle`, { enabled: next });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    try { await api.patch(`/api-keys/${id}/revoke`); toast.success("Key revoked"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const [usage, setUsage] = useState(null);
  const viewUsage = async (id) => {
    try {
      const { data } = await api.get(`/api-keys/${id}/usage`);
      setUsage(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const ownerOptions = (ownerType) =>
    (lookups[ownerType === "company" ? "companies" : "customers"] || []).map((o) => ({ value: o.id, label: o.name }));

  const colspan = cfg.columns.length + (canWrite ? 1 : 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{cfg.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{cfg.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
        {isCurrencies && user.role === "admin" && (
          <Button data-testid="refresh-rates-button" onClick={refreshRates} disabled={refreshing}
            variant="outline" className="border-border bg-card text-foreground hover:bg-accent">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh live rates"}
          </Button>
        )}
        {isCryptoWallets && canWrite && <KeystoreImportButton lookups={lookups} onImported={load} />}
        {canWrite && (
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingId(null); }}>
            <DialogTrigger asChild>
              <Button data-testid={`${resourceKey}-create-button`} onClick={openCreate}
                className="bg-indigo-500 hover:bg-indigo-600 text-white">
                <Plus className="h-4 w-4 mr-2" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">
                  {editingId ? "Edit" : "New"} {cfg.title.replace(/s$/, "")}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {editingId ? "Update the details below and save your changes." : "Fill in the details below to create a new record."}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                {(cfg.fields || []).map((f) => {
                  let options = f.options;
                  if (f.source) {
                    options = (lookups[f.source] || []).map((o) => ({
                      value: o[f.valueKey || "id"], label: o[f.labelKey || "name"],
                    }));
                  }
                  if (f.type === "ownerSelect") options = ownerOptions(form.owner_type);
                  const isSelect = f.type === "select" || f.type === "ownerSelect";
                  return (
                    <div key={f.name} className={`space-y-1.5 ${isSelect || f.type === "text" ? "" : ""}`}>
                      <Label className="text-xs text-muted-foreground">{f.label}{f.required && <span className="text-red-400"> *</span>}</Label>
                      {isSelect ? (
                        <Select value={String(form[f.name] ?? "")} onValueChange={(v) => setField(f.name, v)}>
                          <SelectTrigger data-testid={`field-${f.name}`} className="bg-muted/40 border-border">
                            <SelectValue placeholder={`Select ${f.label}`} />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border text-foreground">
                            {(options || []).map((o) => (
                              <SelectItem key={o.value} value={String(o.value)}
                                className="focus:bg-accent focus:text-accent-foreground">{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input data-testid={`field-${f.name}`} type={f.type === "password" ? "password" : f.type === "number" ? "number" : f.type === "email" ? "email" : "text"}
                          value={form[f.name] ?? ""} onChange={(e) => setField(f.name, e.target.value)}
                          className="bg-muted/40 border-border" placeholder={f.hint || ""} />
                      )}
                    </div>
                  );
                })}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-muted-foreground">Cancel</Button>
                <Button data-testid={`${resourceKey}-save-button`} onClick={submit} disabled={saving}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Save changes" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {isApiKeys && rateAlerts.length > 0 && (
        <div data-testid="rate-limit-alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Rate limit alert</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {rateAlerts.length} active key{rateAlerts.length > 1 ? "s are" : " is"} near or over the per-minute limit:
            </p>
            <ul className="mt-2 space-y-1">
              {rateAlerts.map((r) => (
                <li key={r.id} data-testid="rate-limit-alert-item" className="text-xs flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground">{r.name}</span>
                  <span className="font-mono text-muted-foreground">{r.recent_calls}/{r.rate_limit_per_min} req/min</span>
                  <span className={`font-mono px-1.5 py-0.5 rounded border ${r.pct >= 100 ? "text-red-600 dark:text-red-300 border-red-500/40 bg-red-500/10" : "text-amber-600 dark:text-amber-300 border-amber-500/40 bg-amber-500/10"}`}>
                    {r.pct}%{r.pct >= 100 ? " · throttled (429)" : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {cfg.columns.map((c) => (
                  <TableHead key={c.key} className="text-muted-foreground font-medium text-xs uppercase tracking-wider">{c.label}</TableHead>
                ))}
                {canWrite && <TableHead className="text-right text-muted-foreground text-xs uppercase tracking-wider">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && (
                <TableRow><TableCell colSpan={colspan} className="text-center py-16">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                </TableCell></TableRow>
              )}
              {rows !== null && rows.length === 0 && (
                <TableRow><TableCell colSpan={colspan} className="text-center py-16 text-muted-foreground">
                  <Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No records yet
                </TableCell></TableRow>
              )}
              {rows?.map((row) => (
                <TableRow key={row.id} data-testid={`${resourceKey}-row`} className="border-border hover:bg-accent/60 transition-colors">
                  {cfg.columns.map((c) => (
                    <TableCell key={c.key} className="py-3">
                      {c.render === "toggle" ? (
                        <Switch data-testid="service-toggle-switch" checked={!!row[c.key]}
                          onCheckedChange={(v) => doToggle(row, v)} disabled={!canWrite} />
                      ) : c.render === "secretMasked" ? (
                        row[c.key] ? (
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-xs text-foreground">
                              {revealedSecrets[row.id] ? row[c.key] : "•".repeat(10)}
                            </code>
                            <button type="button" data-testid="secret-reveal-toggle"
                              onClick={() => toggleSecret(row.id)} className="text-muted-foreground hover:text-foreground">
                              {revealedSecrets[row.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        ) : <span className="text-muted-foreground">—</span>
                      ) : c.render === "cryptoDetails" ? (
                        <CryptoTxDetailsButton row={row} />
                      ) : renderCell(c, row)}
                    </TableCell>
                  ))}
                  {canWrite && (
                    <TableCell className="text-right">
                      {isApiKeys && row.status === "active" && (
                        <Button variant="ghost" size="sm" onClick={() => revoke(row.id)}
                          className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 mr-1" data-testid="apikey-revoke">
                          Revoke
                        </Button>
                      )}
                      {isCryptoWallets && <KeystoreDownloadButton row={row} />}
                      {resourceKey === "processors" && <ProcessorKeysButton row={row}
                        onStatusChange={(status) => setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status } : r)))} />}
                      {isApiKeys && (
                        <Button variant="ghost" size="sm" onClick={() => viewUsage(row.id)}
                          className="text-foreground hover:text-foreground hover:bg-muted mr-1" data-testid="apikey-usage">
                          Usage
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openEdit(row)}
                        data-testid={`${resourceKey}-edit`} className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 mr-1">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(row.id)}
                        data-testid={`${resourceKey}-delete`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* API key reveal dialog */}
      <Dialog open={!!revealKey} onOpenChange={(o) => { if (!o) { setRevealKey(null); setCopied(false); } }}>
        <DialogContent className="bg-card border-border text-foreground">
          <DialogHeader><DialogTitle className="font-display">API Key Created</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Copy this secret now — it will not be shown again.</p>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 border border-border p-3">
            <code className="text-xs text-indigo-300 break-all flex-1" data-testid="apikey-secret">{revealKey}</code>
            <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(revealKey); setCopied(true); }}>
              {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => { setRevealKey(null); setCopied(false); }} className="bg-indigo-500 hover:bg-indigo-600 text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API key usage dialog */}
      <Dialog open={!!usage} onOpenChange={(o) => { if (!o) setUsage(null); }}>
        <DialogContent className="bg-card border-border text-foreground max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">API Key Usage</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Total calls: <span className="text-foreground font-mono">{usage?.total ?? 0}</span> · last {usage?.logs?.length ?? 0} shown
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border divide-y divide-border">
            {(!usage?.logs || usage.logs.length === 0) && (
              <p className="text-sm text-muted-foreground p-4 text-center">No calls logged yet.</p>
            )}
            {usage?.logs?.map((l, i) => (
              <div key={i} data-testid="apikey-usage-row" className="flex items-center justify-between px-3 py-2 text-xs">
                <span className="font-mono text-foreground">{l.endpoint}</span>
                <span className="font-mono text-emerald-300">{l.status}</span>
                <span className="text-muted-foreground">{fmtDateShort(l.timestamp)}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtDateShort(v) {
  try { return new Date(v).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return v; }
}
